import React, { useCallback, useEffect, useState } from "react";
import Map, { Marker } from "react-map-gl";

import SearchAddress from "./SearchAddress";
import FindMyLocation from "./FindMyLocation";

import NewDialog from "components/NewDialog";
import NewDialogTitle from "components/NewDialogTitle";
import NewDialogContent from "components/NewDialogContent";
import NewDialogActions from "components/NewDialogActions";

import { mapboxAccessToken, mapirApiToken } from "config";
import { useGeolocation } from "hooks/geoHook";
import { FormattedMessage } from "react-intl";
import {
  Box,
  Card,
  Typography,
  CardActionArea,
  Stack,
  styled,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";

// import "./styles.css";
import "mapbox-gl/dist/mapbox-gl.css";

const MAP_CONTAINER_STYLE = { width: "100%", height: "min(65vh, 620px)" };
const OSM_MAP_STYLE = {
  version: 8,
  sources: {
    openStreetMap: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "openStreetMap",
      type: "raster",
      source: "openStreetMap",
    },
  ],
};

const getCoordinate = (value, fallback) => {
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : fallback;
};

const sameViewState = (current, next) =>
  current.longitude === next.longitude &&
  current.latitude === next.latitude &&
  current.zoom === next.zoom &&
  (current.bearing || 0) === (next.bearing || 0) &&
  (current.pitch || 0) === (next.pitch || 0);

const LocationFieldTemplate = (props) => {
  const {
    schema,
    formData,
    onChange,
    disabled,
    zoom = [15],
    readonly,
    rawErrors,
    uiSchema,
  } = props;
  const { confirmLocationChange = false } = uiSchema?.["ui:options"] || {};
  const state = useGeolocation();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [center, setCenter] = useState([state.longitude, state.latitude]);
  const [viewState, setViewState] = useState({
    longitude: state.longitude,
    latitude: state.latitude,
    zoom: Number(Array.isArray(zoom) ? zoom[0] : zoom) || 15,
  });
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [address, setAddress] = useState(formData?.address || "");
  const [addressLoading, setAddressLoading] = useState(false);

  const onOpen = () => {
    const nextCenter = [
      getCoordinate(formData?.lng, state.longitude),
      getCoordinate(formData?.lat, state.latitude),
    ];
    setCenter(nextCenter);
    setViewState((current) => ({
      ...current,
      longitude: nextCenter[0],
      latitude: nextCenter[1],
    }));
    setOpen(true);
  };
  const onClose = () => {
    setConfirmOpen(false);
    setOpen(false);
  };

  const handleCenter = (lng, lat) => {
    const nextLng = Number(lng);
    const nextLat = Number(lat);

    if (Number.isFinite(nextLng) && Number.isFinite(nextLat)) {
      setCenter([nextLng, nextLat]);
      setViewState((current) => ({
        ...current,
        longitude: nextLng,
        latitude: nextLat,
      }));
    }
  };

  const handleMapClick = (event) =>
    handleCenter(event.lngLat.lng, event.lngLat.lat);

  const handleMove = useCallback((event) => {
    const next = event.viewState;
    setViewState((current) => (sameViewState(current, next) ? current : next));
  }, []);

  const findAddress = async (lng, lat) => {
    const response = await fetch(`https://map.ir/reverse/no?lat=${lat}&lon=${lng}`, {
      headers: {
        "x-api-key": mapirApiToken,
        "Mapir-SDK": "reactjs",
        "Content-Type": "application/json",
        "Accept-Language": "fa",
      },
    });

    if (!response.ok) {
      throw new Error(`Map.ir reverse geocoding failed with status ${response.status}`);
    }

    const data = await response.json();
    return data?.address_compact || data?.address || "";
  };

  useEffect(() => {
    const lat = Number(formData?.lat);
    const lng = Number(formData?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setAddress("");
      return undefined;
    }

    if (formData?.address) {
      setAddress(formData.address);
      return undefined;
    }

    let active = true;
    setAddressLoading(true);
    findAddress(lng, lat)
      .then((result) => {
        if (active) setAddress(result);
      })
      .catch(() => {
        if (active) setAddress("");
      })
      .finally(() => {
        if (active) setAddressLoading(false);
      });

    return () => {
      active = false;
    };
  }, [formData?.lat, formData?.lng, formData?.address]);

  const applyLocation = async () => {
    const [lng, lat] = center;
    setAddressLoading(true);

    let selectedAddress = "";
    try {
      selectedAddress = await findAddress(lng, lat);
    } catch (error) {
      selectedAddress = "";
    }

    onChange({
      ...(formData || {}),
      lat: String(lat),
      lng: String(lng),
      address: selectedAddress,
    });
    setAddress(selectedAddress);
    setAddressLoading(false);
    setLocationConfirmed(true);
    setConfirmOpen(false);
    onClose();
  };

  const handleConfirm = () => {
    if (confirmLocationChange) {
      setConfirmOpen(true);
      return;
    }
    applyLocation();
  };

  return (
    <>
      <Card
        dir="rtl"
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor:
            rawErrors && rawErrors.length > 0
              ? "error"
              : "action.disabledBackground",
        }}
      >
        <CardActionArea
          onClick={onOpen}
          disabled={disabled || readonly}
          focusRipple={rawErrors && rawErrors.length > 0}
          style={{ direction: "rtl", textAlign: "right" }}
        >
          <Stack
            dir="rtl"
            style={{ direction: "rtl", textAlign: "right" }}
            alignContent="center"
            alignItems="center"
            direction="row"
            minHeight={64}
            px={1.5}
            columnGap={0.5}
          >
            <Stack flexGrow={1} alignItems="flex-start" justifyContent="center">
              <Typography
                align="right"
                variant="body1"
                color="textSecondary"
                style={{ direction: "rtl", textAlign: "right" }}
              >
                <FormattedMessage id={schema.title} defaultMessage={schema.title} />
              </Typography>
              {addressLoading && <CircularProgress size={14} sx={{ mt: 0.5 }} />}
              {!addressLoading && address && (
                <Typography
                  noWrap
                  variant="caption"
                  color="text.secondary"
                  title={address}
                  sx={{ width: "100%", maxWidth: 520 }}
                  style={{ direction: "rtl", textAlign: "right" }}
                >
                  {address}
                </Typography>
              )}
            </Stack>
            {locationConfirmed && (
              <Typography
                color="success.main"
                variant="subtitle2"
                style={{ direction: "rtl", textAlign: "right" }}
              >
                <FormattedMessage id="location_selected" />
              </Typography>
            )}
            {/* <CalendarMonthRoundedIcon color="disabled" fontSize="small" /> */}
          </Stack>
        </CardActionArea>
      </Card>
      <NewDialog label="select-location" open={open} onClose={onClose} maxWidth="lg" disableFullScreen>
        <NewDialogTitle onClose={onClose} title={<FormattedMessage id="select_location" />} />
        <NewDialogContent isPopup>
          <Stack p={1} flex={1} rowGap={1} bgcolor="background.paper">
            <Card variant="outlined">
              <Box
                width="100%"
                height="100%"
                overflow="hidden"
                bgcolor="#f9f9f9"
                position="relative"
                borderRadius={1}
              >
                <Map
                  mapboxAccessToken={mapboxAccessToken}
                  minZoom={6}
                  maxZoom={20}
                  bearingSnap={10}
                  renderWorldCopies={false}
                  attributionControl
                  reuseMaps
                  mapStyle={OSM_MAP_STYLE}
                  {...viewState}
                  onMove={handleMove}
                  onClick={handleMapClick}
                  style={MAP_CONTAINER_STYLE}
                >
                  <Marker longitude={center[0]} latitude={center[1]} anchor="center">
                    <StyledBox
                      width={16}
                      height={16}
                      borderRadius={1}
                      sx={{ opacity: 0.9 }}
                      color="primary"
                      bgcolor="primary.main"
                    />
                  </Marker>
                </Map>
                <Stack
                  p={0.5}
                  top={0}
                  left={0}
                  right={0}
                  zIndex={100}
                  rowGap={0.5}
                  position="absolute"
                >
                  <SearchAddress
                    changeCenter={(data) => handleCenter(data[0], data[1])}
                  />
                </Stack>
                <Stack
                  p={0.5}
                  bottom={0}
                  // left={0}
                  right={0}
                  zIndex={100}
                  columnGap={0.5}
                  position="absolute"
                  alignItems="center"
                  direction="row-reverse"
                >
                  <FindMyLocation
                    onClick={(data) => handleCenter(data[0], data[1])}
                  />
                </Stack>
              </Box>
            </Card>
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Button color="primary" variant="contained" onClick={handleConfirm}>
            <FormattedMessage id="confirm" />
          </Button>
        </NewDialogActions>
      </NewDialog>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        dir="rtl"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <FormattedMessage id="select_location" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="confirm_location_change" />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button color="primary" variant="contained" disabled={addressLoading} onClick={applyLocation}>
            <FormattedMessage id="confirm" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const StyledBox = styled(Box)(({ theme, color = "primary" }) => ({
  "&::after": {
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    position: "absolute",
    border: "1px solid",
    animation: "ripple 1.2s infinite ease-in-out",
    borderColor: theme.palette[color].main,
    content: '""',
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

export default LocationFieldTemplate;
